import React, { useState, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Text } from '@react-three/drei';
import * as THREE from 'three';

const ExcavationViewer = () => {
  const [dimensions, setDimensions] = useState({
    terrainLength: 8,
    terrainWidth: 6,
    terrainHeight: 2,
    holeLength: 4,
    holeWidth: 3,
    holeDepth: 1.2
  });

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    const numericValue = parseFloat(value) || 0.1;
    const roundedValue = Math.max(0.1, Math.round(numericValue * 10) / 10);
    
    setDimensions(prev => {
      const newDims = {...prev, [name]: roundedValue};
      
      // Asegurar que el hueco no sea mayor que el terreno
      if (name === 'terrainLength') {
        newDims.holeLength = Math.min(newDims.holeLength, roundedValue);
      } else if (name === 'terrainWidth') {
        newDims.holeWidth = Math.min(newDims.holeWidth, roundedValue);
      } else if (name === 'terrainHeight') {
        newDims.holeDepth = Math.min(newDims.holeDepth, roundedValue * 0.9);
      } else if (name.startsWith('hole')) {
        const terrainDim = 'terrain' + name.replace('hole', '');
        const maxValue = name === 'holeDepth' ? prev[terrainDim] * 0.9 : prev[terrainDim];
        newDims[name] = Math.min(roundedValue, maxValue);
      }
      
      return newDims;
    });
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      fontFamily: 'Arial, sans-serif'
    }}>
      <header style={{ 
        backgroundColor: '#2c3e50', 
        color: 'white', 
        padding: '1rem',
        textAlign: 'center'
      }}>
        <h1 style={{ margin: 0 }}>Visualizador de Excavación</h1>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>Ajusta los controles para modificar las dimensiones</p>
      </header>

      <div style={{ 
        display: 'flex', 
        flex: 1, 
        overflow: 'hidden'
      }}>
        <div style={{ flex: 1 }}>
          <Canvas shadows camera={{ position: [0, 5, 10], fov: 50 }}>
            <ambientLight intensity={0.5} />
            <spotLight 
              position={[10, 20, 10]} 
              angle={0.3} 
              intensity={1} 
              castShadow
              shadow-mapSize-width={2048}
              shadow-mapSize-height={2048}
            />
            <OrbitControls 
              enablePan={true} 
              enableZoom={true} 
              enableRotate={true}
              minDistance={5}
              maxDistance={20}
            />
            <ExcavationScene dimensions={dimensions} />
          </Canvas>
        </div>

        <div style={{ 
          width: '300px', 
          backgroundColor: '#f8f9fa', 
          padding: '1rem',
          overflowY: 'auto',
          borderLeft: '1px solid #ddd'
        }}>
          <DimensionControls 
            dimensions={dimensions} 
            onChange={handleDimensionChange} 
          />
        </div>
      </div>
    </div>
  );
};

const DimensionControls = ({ dimensions, onChange }) => {
  const controls = [
    {
      title: "Dimensiones del Terreno",
      params: [
        {name: 'terrainLength', label: 'Longitud', max: 15},
        {name: 'terrainWidth', label: 'Ancho', max: 12},
        {name: 'terrainHeight', label: 'Altura', max: 4}
      ]
    },
    {
      title: "Dimensiones del Hueco",
      params: [
        {name: 'holeLength', label: 'Longitud', terrain: 'terrainLength'},
        {name: 'holeWidth', label: 'Ancho', terrain: 'terrainWidth'},
        {name: 'holeDepth', label: 'Profundidad', terrain: 'terrainHeight'}
      ]
    }
  ];

  return (
    <>
      {controls.map((group, i) => (
        <div key={i}>
          <h2 style={{ 
            marginTop: i > 0 ? '1.5rem' : 0,
            marginBottom: '1rem',
            color: '#2c3e50',
            borderBottom: '1px solid #ddd',
            paddingBottom: '0.5rem'
          }}>
            {group.title}
          </h2>
          
          {group.params.map((param) => (
            <div key={param.name} style={{ marginBottom: '1.2rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.4rem',
                fontWeight: 'bold',
                color: '#34495e',
                fontSize: '0.9rem'
              }}>
                {param.label} (m): {dimensions[param.name]}
              </label>
              <input
                type="range"
                min="0.5"
                max={param.max || dimensions[param.terrain] * (param.name === 'holeDepth' ? 0.9 : 1)}
                step="0.1"
                name={param.name}
                value={dimensions[param.name]}
                onChange={onChange}
                style={{ 
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  background: '#e9ecef',
                  outline: 'none'
                }}
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginTop: '0.3rem',
                fontSize: '0.8rem',
                color: '#6c757d'
              }}>
                <span>0.5m</span>
                <span>{param.max || (dimensions[param.terrain] * (param.name === 'holeDepth' ? 0.9 : 1)).toFixed(1)}m</span>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
};

const ExcavationScene = ({ dimensions }) => {
  const {
    terrainLength,
    terrainWidth,
    terrainHeight,
    holeLength,
    holeWidth,
    holeDepth
  } = dimensions;

  const textRefs = useRef([]);

  useFrame(({ camera }) => {
    textRefs.current.forEach(ref => {
      if (ref) ref.lookAt(camera.position);
    });
  });

  // Materiales
  const terrainMaterial = new THREE.MeshStandardMaterial({ 
    color: '#8b5a2b',
    side: THREE.DoubleSide,
    roughness: 0.7,
    metalness: 0.1
  });

  const holeMaterial = new THREE.MeshStandardMaterial({
    color: '#3498db',
    transparent: true,
    opacity: 0.8,
    side: THREE.DoubleSide
  });

  // Crear geometría del terreno con hueco visible
  const terrainGeometry = new THREE.BoxGeometry(terrainLength, terrainHeight, terrainWidth);
  const holeGeometry = new THREE.BoxGeometry(holeLength, holeDepth + 0.01, holeWidth);

  // Posición del hueco (centrado en el terreno)
  const holePosition = new THREE.Vector3(
    0, 
    (terrainHeight/2) - (holeDepth/2), 
    0
  );

  return (
    <group>
      {/* Terreno principal */}
      <mesh
        position={[0, 0, 0]}
        geometry={terrainGeometry}
        material={terrainMaterial}
        castShadow
        receiveShadow
      />

      {/* Hueco de excavación (cortado en el terreno) */}
      <mesh
        position={holePosition}
        geometry={holeGeometry}
        material={holeMaterial}
      >
        {/* Agregamos una luz dentro del hueco para mejor visibilidad */}
        <pointLight 
          position={[0, 0, 0]} 
          color="#a0d8ff" 
          intensity={0.5} 
          distance={holeLength}
        />
      </mesh>

      {/* Borde del terreno */}
      <lineSegments>
        <edgesGeometry args={[terrainGeometry]} />
        <lineBasicMaterial color="#2c3e50" linewidth={2} />
      </lineSegments>

      {/* Borde del hueco (resaltado) */}
      <lineSegments position={holePosition}>
        <edgesGeometry args={[holeGeometry]} />
        <lineBasicMaterial color="#ffffff" linewidth={2} />
      </lineSegments>

      {/* Medidas del terreno */}
      <group ref={el => textRefs.current[0] = el} 
        position={[0, terrainHeight/2 + 0.2, -terrainWidth/2 - 0.5]}>
        <Text 
          color="#ffffff"
          fontSize={0.3}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="#000000"
        >
          {terrainLength.toFixed(1)}m
        </Text>
      </group>

      <group ref={el => textRefs.current[1] = el} 
        position={[terrainLength/2 + 0.5, terrainHeight/2 + 0.2, 0]}>
        <Text 
          color="#ffffff"
          fontSize={0.3}
          anchorX="center"
          anchorY="middle"
          rotation={[0, Math.PI/2, 0]}
          outlineWidth={0.02}
          outlineColor="#000000"
        >
          {terrainWidth.toFixed(1)}m
        </Text>
      </group>

      {/* Medidas del hueco */}
      <group ref={el => textRefs.current[2] = el} 
        position={[0, holePosition.y + holeDepth/2 + 0.1, 0]}>
        <Text 
          color="#ffffff"
          fontSize={0.2}
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.01}
          outlineColor="#000000"
        >
          {holeLength.toFixed(1)} × {holeWidth.toFixed(1)} × {holeDepth.toFixed(1)}m
        </Text>
      </group>
    </group>
  );
};

export default ExcavationViewer;